import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useAuth } from "../../lib/auth/useAuth";
import { useOrg } from "../../lib/org/useOrg";
import { hashInviteToken } from "../../lib/org/invites";
import { isSupabaseConfigured, supabase } from "../../lib/supabase";

const E2E_BYPASS = import.meta.env.VITE_E2E_BYPASS_AUTH === "true";

// Error codes raised by the `accept_invitation` RPC in migration 0009.
// Kept in sync with the plpgsql exception `using errcode = …` clauses.
const ERR_EXPIRED = "P7401";
const ERR_EMAIL_MISMATCH = "P7402";
const ERR_ALREADY_ACCEPTED = "P7403";

function messageForCode(code: string | undefined): string {
  switch (code) {
    case ERR_EXPIRED:
      return "This invitation has expired. Ask the inviter to send a new one.";
    case ERR_EMAIL_MISMATCH:
      return "This invitation was issued to a different email address. Sign in with the invited address to accept.";
    case ERR_ALREADY_ACCEPTED:
      return "This invitation has already been accepted.";
    default:
      return "We couldn't accept this invitation. Please ask the inviter to resend.";
  }
}

export function AuthCallback() {
  const navigate = useNavigate();
  const { session, loading } = useAuth();
  const { setActiveOrg } = useOrg();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");

  const [error, setError] = useState<string | null>(null);
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (loading) return;
    if (attemptedRef.current) return;

    // Bypass mode: skip the RPC entirely.
    if (E2E_BYPASS) {
      attemptedRef.current = true;
      navigate("/inbox", { replace: true });
      return;
    }

    if (!session) {
      // Token exchange failed or expired — fall back to login.
      attemptedRef.current = true;
      navigate("/login", { replace: true });
      return;
    }

    // No invite — normal magic-link flow.
    if (!inviteToken) {
      attemptedRef.current = true;
      navigate("/inbox", { replace: true });
      return;
    }

    attemptedRef.current = true;
    void (async () => {
      try {
        if (!isSupabaseConfigured) {
          throw new Error("Supabase is not configured");
        }
        const hash = await hashInviteToken(inviteToken);
        const { data, error: rpcError } = await supabase.rpc(
          "accept_invitation",
          { token_hash: hash },
        );
        if (rpcError) {
          // PostgREST surfaces plpgsql error codes through `.code`.
          const code = (rpcError as { code?: string }).code;
          setError(messageForCode(code));
          return;
        }
        const orgId = typeof data === "string" ? data : null;
        if (orgId) setActiveOrg(orgId);
        navigate("/inbox", { replace: true });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to accept invitation",
        );
      }
    })();
  }, [loading, session, inviteToken, navigate, setActiveOrg]);

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--ink-50)] flex items-center justify-center px-4">
        <div
          role="alert"
          className="max-w-md w-full bg-white border border-[var(--ink-200)] rounded-xl p-6 text-center space-y-4"
        >
          <h1 className="text-lg font-semibold text-[var(--ink-950)]">
            Invitation error
          </h1>
          <p className="text-sm text-[var(--ink-700)]">{error}</p>
          <button
            type="button"
            onClick={() => navigate("/login", { replace: true })}
            className="inline-flex items-center bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--ink-50)] flex items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-[var(--ink-700)]">
        <div
          role="status"
          aria-label="Signing you in"
          className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--ink-200)] border-t-[var(--brand-600)]"
        />
        <div className="text-sm">
          {inviteToken ? "Accepting invitation…" : "Signing you in…"}
        </div>
      </div>
    </div>
  );
}
